/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Worktree sidebar component for the Changes page
 */

import React, { useRef, useCallback, useEffect, useState } from 'react';
import { GitBranch, RefreshCw, Loader, ChevronLeft, ChevronRight, GripVertical, Trash2 } from 'lucide-react';
import type { WorktreeInfo } from './types';

interface WorktreeSidebarProps {
  worktrees: WorktreeInfo[];
  selectedWorktree: WorktreeInfo | null;
  onSelectWorktree: (worktree: WorktreeInfo) => void;
  onRefresh: () => void;
  loading: boolean;
  refreshing: boolean;
  repositoryUrl?: string;
  /** Width of the sidebar in pixels */
  width?: number;
  /** Callback when width changes via resize */
  onWidthChange?: (width: number) => void;
  /** Whether the sidebar is collapsed */
  isCollapsed?: boolean;
  /** Callback when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Callback when delete is requested for a worktree */
  onDeleteWorktree?: (worktree: WorktreeInfo) => void;
}

const MIN_WIDTH = 200;
const MAX_WIDTH = 400;
const COLLAPSED_WIDTH = 48;

export function WorktreeSidebar({
  worktrees,
  selectedWorktree,
  onSelectWorktree,
  onRefresh,
  loading,
  refreshing,
  repositoryUrl,
  width = 256,
  onWidthChange,
  isCollapsed = false,
  onCollapsedChange,
  onDeleteWorktree
}: WorktreeSidebarProps) {
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !sidebarRef.current) return;
    
    const sidebarRect = sidebarRef.current.getBoundingClientRect();
    const newWidth = e.clientX - sidebarRect.left;
    
    if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH && onWidthChange) {
      onWidthChange(newWidth);
    }
  }, [isResizing, onWidthChange]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleToggleCollapse = useCallback(() => {
    if (onCollapsedChange) {
      onCollapsedChange(!isCollapsed);
    }
  }, [isCollapsed, onCollapsedChange]);

  // Collapsed view
  if (isCollapsed) {
    return (
      <div 
        className="bg-white flex flex-col items-center py-4 changes-page-sidebar"
        style={{ width: `${COLLAPSED_WIDTH}px` }}
      >
        <GitBranch size={20} className="text-gray-600 mb-3" />
        <button
          onClick={handleToggleCollapse}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
          title="Expand sidebar"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <div 
      ref={sidebarRef}
      className="bg-white flex flex-col changes-page-sidebar relative"
      style={{ width: `${width}px` }}
    >
      {/* Sidebar Header */}
      <div className="px-4 py-4 changes-page-header">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900">Worktrees</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={onRefresh}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
              title="Refresh worktrees"
            >
              <RefreshCw size={16} className={loading || refreshing ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={handleToggleCollapse}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
              title="Collapse sidebar"
            >
              <ChevronLeft size={16} />
            </button>
          </div>
        </div>
        
        {repositoryUrl && (
          <div className="text-xs text-gray-600 break-all mt-1">
            {repositoryUrl.split('/').slice(-2).join('/')}
          </div>
        )}
      </div>

      {/* Worktree List */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader size={24} className="animate-spin text-blue-600" />
          </div>
        ) : worktrees.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {worktrees.map((wt) => {
              const isSelected = selectedWorktree?.path === wt.path;
              const name = wt.path.split('/').pop() || wt.path;
              const isMainWorktree = name === 'velocity';
              
              return (
                <div
                  key={wt.path}
                  className={`w-full text-left p-3 hover:bg-gray-50 transition-colors group relative ${
                    isSelected ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                  }`}
                >
                  <button
                    onClick={() => onSelectWorktree(wt)}
                    className="w-full text-left"
                  >
                    <div className="flex items-start gap-2">
                      <GitBranch 
                        size={16} 
                        className={`mt-0.5 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`}
                      />
                      <div className="flex-1 min-w-0 pr-6">
                        <div className={`text-sm font-medium truncate ${
                          isSelected ? 'text-blue-900' : 'text-gray-900'
                        }`}>
                          {name}
                          {isMainWorktree && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                              main
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 truncate mt-0.5">
                          {wt.branch}
                        </div>
                        <div className="text-xs text-gray-500 truncate mt-0.5">
                          {wt.commit.substring(0, 7)}
                        </div>
                      </div>
                    </div>
                  </button>
                  
                  {/* Delete button - only show for non-main worktrees */}
                  {!isMainWorktree && onDeleteWorktree && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteWorktree(wt);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete worktree"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center h-32 text-gray-500 text-sm px-4 text-center">
            No worktrees found
          </div>
        )}
      </div>

      {/* Resize Handle */}
      <div
        ref={resizeRef}
        className="worktree-sidebar-resize-handle"
        onMouseDown={handleMouseDown}
        style={{ 
          opacity: isResizing ? 1 : undefined,
          background: isResizing ? '#d1d5db' : undefined
        }}
      >
        <div className="worktree-sidebar-resize-grip">
          <GripVertical size={12} className="text-gray-400" />
        </div>
      </div>
    </div>
  );
}

